import { useEffect, useRef, useState } from "react";
import styles from "./ProgressBar.module.css";
import { cn } from "src/utils/cn";

function rangeStringToProgress(
  /** A string value between 0 and 100. */
  progressPercentString: string,
  fallbackValue = 0
) {
  const valuePercent = Number(progressPercentString);

  if (isNaN(valuePercent)) {
    return fallbackValue;
  }

  return valuePercent / 100;
}

type ProgressBarProps = {
  /** A number between 0 and 1 */
  progress: number;
  onSeek: (
    /** A number between 0 and 1 */
    progress: number
  ) => void;
  /** Whether the video is currently playing. Used to determine if we have to
   * play after seeking. */
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
};

export function ProgressBar({
  progress,
  onSeek,
  isPlaying,
  pause,
  play,
}: ProgressBarProps) {
  const scrubberRef = useRef<HTMLInputElement>(null);
  const [wasPlayingBeforeScrubbing, setWasPlayingBeforeScrubbing] =
    useState(false);

  const progressPercent = progress * 100;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.stopPropagation();

    onSeek(rangeStringToProgress(e.currentTarget.value));
  }

  function handlePointerDown(e: React.PointerEvent<HTMLInputElement>) {
    e.stopPropagation();
    setWasPlayingBeforeScrubbing(isPlaying);

    if (isPlaying) {
      pause();
    }

    onSeek(rangeStringToProgress(e.currentTarget.value));
  }

  function handlePointerUp() {
    if (wasPlayingBeforeScrubbing) {
      play();
    }
  }

  useEffect(() => {
    if (!scrubberRef.current) {
      return;
    }

    // Adjusts progress bar length to align with the thumb circle' center
    // (0.5rem in diameter). Initially, the bar extends an extra 0.25rem to
    // align with the circle's center. As progress increases, this extra length
    // linearly decreases to 0 at 100% progress.

    // The thumb's radius in rem
    const thumbRadius = 0.25;
    const additionalPercentage = thumbRadius * (1 - 2 * progress);

    // Adjust the background size
    scrubberRef.current.style.backgroundSize = `calc(${
      progress * 100
    }% + ${additionalPercentage}rem) 100%`;
  }, [progress]);

  return (
    <div
      className={cn(
        "group relative flex h-6 w-full items-center sm:h-8",
        styles["progress-bar"]
      )}
    >
      <input
        ref={scrubberRef}
        className="h-2 w-full cursor-pointer bg-gray-200 drop-shadow-sm"
        type="range"
        value={progressPercent}
        min={0}
        max={100}
        onChange={handleChange}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      />
    </div>
  );
}
